import React from 'react';
import { useParams, NavLink, Outlet, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Award, BookOpen, Loader2 } from 'lucide-react';
import { useGetKvsResult9n11StudentDetailQuery } from '../../../services/kvsResult9n11Api';
import { computeLevel } from '../data/index';

const tabs = [
  { label: 'Overview', tab: 'overview' },
  { label: 'Academic', tab: 'academic' },
];

const StudentProfile: React.FC = () => {
  const { studentId } = useParams<{ studentId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const academicYear = Number(searchParams.get('year') || '2025');
  // Class 9/11 results carry no term dimension — always query "overall" (0).
  const term = 0;

  const { data: studentRes, isLoading } = useGetKvsResult9n11StudentDetailQuery({
    year: academicYear,
    term,
    studentid: Number(studentId) || 0,
  });

  const rawData = studentRes?.data;
  const d = Array.isArray(rawData) ? rawData[0] : rawData;

  const fullName = d ? [d.firstName, d.middleName, d.lastName].filter(Boolean).join(' ') || d.studentName || 'Student Name' : '';
  const attendancePct = d && d.workingDay > 0 ? +((d.presentDay / d.workingDay) * 100).toFixed(1) : 0;
  const level = d ? computeLevel(d.marks) : '';

  const pathParts = location.pathname.split('/');
  const lastPart = pathParts[pathParts.length - 1];
  const tabNames = tabs.map(t => t.tab);
  const basePath = tabNames.includes(lastPart) ? pathParts.slice(0, -1).join('/') : location.pathname;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
        <span className="ml-3 text-gray-500">Loading student profile…</span>
      </div>
    );
  }

  if (!d) {
    return (
      <div className="p-12 text-center">
        <p className="text-gray-500">Student not found</p>
        <button onClick={() => navigate(-1)} className="mt-4 text-indigo-600 underline text-sm">Go Back</button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-indigo-600 transition mb-4">
        <ArrowLeft size={15} /> Back
      </button>

      {/* Profile Header */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6">
        <div className="flex flex-wrap items-start gap-6">
          <div className="w-20 h-20 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-2xl font-bold flex-shrink-0">
            {fullName.split(' ').map((n: string) => n[0]).join('')}
          </div>

          <div className="flex-1 min-w-[200px]">
            <h1 className="text-xl font-bold text-gray-900">{fullName}</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              ID: {d.studentID} &middot; Class {d.class} &middot; Sec {d.section} &middot; Roll #{d.rollNo}
            </p>
            <p className="text-xs text-blue-600 font-medium mt-1">{d.schoolName}</p>
            <div className="flex flex-wrap gap-4 mt-3">
              <div className="flex items-center gap-1.5 text-sm">
                <Award size={14} className="text-blue-500" />
                <span className="text-gray-600">Level: <span className="font-semibold text-gray-900">{level}</span></span>
              </div>
              <div className="flex items-center gap-1.5 text-sm">
                <BookOpen size={14} className="text-emerald-500" />
                <span className="text-gray-600">Marks: <span className="font-semibold text-gray-900">{d.marks}%</span></span>
              </div>
              <div className="flex items-center gap-1.5 text-sm">
                <BookOpen size={14} className="text-indigo-500" />
                <span className="text-gray-600">Attendance: <span className="font-semibold text-gray-900">{attendancePct}%</span></span>
              </div>
            </div>
          </div>

          {d.marks < 60 && (
            <div className="px-3 py-1.5 bg-red-50 border border-red-200 rounded-full text-xs font-semibold text-red-600">
              Needs Support
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-1 -mb-px overflow-x-auto">
          {tabs.map(t => (
            <NavLink
              key={t.tab}
              to={`${basePath}/${t.tab}${location.search}`}
              className={({ isActive }) =>
                `px-4 py-2.5 text-sm font-medium border-b-2 transition whitespace-nowrap ${
                  isActive ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`
              }
            >
              {t.label}
            </NavLink>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <Outlet context={{ student: d, fullName, attendancePct, level }} />
    </div>
  );
};

export default StudentProfile;
